/**
 * An item's or a case's history, read newest first a page at a time.
 *
 * The first page (the newest rows) loads on mount and again whenever
 * `revision` changes (the host passes the record's updated_at, so an action
 * that writes an event refetches it). "Show older" appends the next page
 * from the server's cursor; `has_more` comes from the last page read, so the
 * window always says honestly whether older rows exist (deliveryView
 * .timelineIsCurrent reads it).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { HistoryPage, HistoryQuery } from '../../services/qcpInvestigations';

export interface HistoryPages<E> {
  /** null while the first page loads; 'error' when it failed. */
  window: { events: E[]; order: 'desc'; has_more: boolean } | 'error' | null;
  /** Fetch the next older page (no-op when none is left or one is loading). */
  showOlder: () => void;
  loadingOlder: boolean;
  /** Why the last "Show older" failed; cleared by the next try. */
  olderError: string | null;
}

interface Loaded<E> {
  /** The record and revision these rows were read for. */
  key: string;
  id: string;
  events: E[] | 'error';
  hasMore: boolean;
  nextCursor: string | null;
}

interface OlderState {
  key: string;
  loading: boolean;
  error: string | null;
}

export function useHistoryPages<E>(
  fetchPage: (q: HistoryQuery) => Promise<HistoryPage<E>>,
  id: string,
  revision: string,
): HistoryPages<E> {
  const key = `${id}@${revision}`;
  const [loaded, setLoaded] = useState<Loaded<E> | null>(null);
  const [older, setOlder] = useState<OlderState | null>(null);
  // The host passes a fresh closure each render; read the latest one when
  // fetching, without refetching on identity (declared before the fetch
  // effect so it is current when that runs).
  const fetchRef = useRef(fetchPage);
  useEffect(() => {
    fetchRef.current = fetchPage;
  });

  useEffect(() => {
    let cancelled = false;
    fetchRef
      .current({})
      .then((page) => {
        if (!cancelled) {
          setLoaded({ key, id, events: page.events, hasMore: page.has_more, nextCursor: page.next_cursor });
        }
      })
      .catch(() => {
        if (!cancelled) setLoaded({ key, id, events: 'error', hasMore: false, nextCursor: null });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands for id + revision
  }, [key]);

  // Rows of another record never show; a newer revision of the same record
  // keeps showing the old rows until its first page lands.
  const current = loaded && loaded.id === id ? loaded : null;
  const olderNow = older && current && older.key === current.key ? older : null;

  const showOlder = useCallback(() => {
    if (!current || current.events === 'error' || !current.hasMore || !current.nextCursor) return;
    if (olderNow?.loading) return;
    const base = current;
    setOlder({ key: base.key, loading: true, error: null });
    fetchRef
      .current({ cursor: base.nextCursor })
      .then((page) => {
        setLoaded((prev) =>
          prev && prev.key === base.key && Array.isArray(prev.events)
            ? {
                ...prev,
                events: [...prev.events, ...page.events],
                hasMore: page.has_more,
                nextCursor: page.next_cursor,
              }
            : prev,
        );
        setOlder({ key: base.key, loading: false, error: null });
      })
      .catch(() => {
        setOlder({ key: base.key, loading: false, error: 'Could not load older history. Please try again.' });
      });
  }, [current, olderNow]);

  let view: HistoryPages<E>['window'] = null;
  if (current) {
    view =
      current.events === 'error'
        ? 'error'
        : { events: current.events, order: 'desc', has_more: current.hasMore };
  }
  return {
    window: view,
    showOlder,
    loadingOlder: olderNow?.loading ?? false,
    olderError: olderNow?.error ?? null,
  };
}
